// Armadores de los documentos de las decisiones iniciales que ya tienen plantilla:
// - Indagación previa (Art. 208)  → INDAGACION-PREVIA.docx
// - Auto de apertura de investigación (Art. 211-213) → AUTO-APERTURA-INVESTIGACION.docx
// Prellenan con los datos del expediente; el contratista edita, previsualiza y descarga.
// Sigue la convención del Auto Inhibitorio (ver auto-inhibitorio.ts). El mapeo fino de
// los campos del cuerpo de cada minuta se afinará después.
import type { ExpedienteDetalle } from '@/lib/expedientes';
import { generarDocx, renderDocxBlob } from './motor';
import { PLANTILLAS, type PlantillaId } from './registro';
import { fechaEnLetras } from './fechas';

const MESES = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
];
const hoyISO = () => new Date().toISOString().slice(0, 10);
function fechaCorta(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso.length <= 10 ? `${iso}T00:00:00` : iso);
  return isNaN(d.getTime()) ? '' : `${d.getDate()} de ${MESES[d.getMonth()]} de ${d.getFullYear()}`;
}

export interface CampoDoc {
  key: string;
  label: string;
  multiline?: boolean;
}

export type DatosDoc = Record<string, string>;

// Datos base tomados del expediente, comunes a ambas minutas.
function base(exp: ExpedienteDetalle): DatosDoc {
  const inv = exp.investigados?.[0];
  const radicado = exp.radicado_manual || exp.radicado || '';
  const investigado =
    inv?.nombre || (inv?.tipo === 'POR_ESTABLECER' ? 'Por individualizar' : 'Por determinar');
  const cargoEnt =
    [inv?.cargo, inv?.entidad_nombre].filter(Boolean).join(' / ') || 'Por determinar';
  return {
    RADICADO: radicado,
    INVESTIGADO: investigado,
    CARGO_ENTIDAD: cargoEnt,
    FECHA_QUEJA: fechaCorta(exp.fecha_queja),
    CIUDAD: 'Pereira',
    FECHA_LETRAS: fechaEnLetras(hoyISO()),
  };
}

// Configuración de cada documento de decisión (para el diálogo genérico).
export interface DocDecisionConfig {
  plantillaId: PlantillaId;
  titulo: string;
  campos: CampoDoc[];
  prefill: (exp: ExpedienteDetalle) => DatosDoc;
}

const CAMPOS_INDAGACION: CampoDoc[] = [
  { key: 'RADICADO', label: 'Radicado / Expediente No.' },
  { key: 'INVESTIGADO', label: 'Investigado(a)' },
  { key: 'CARGO_ENTIDAD', label: 'Cargo y entidad' },
  { key: 'FECHA_QUEJA', label: 'Fecha de la queja / informe' },
  { key: 'CIUDAD', label: 'Ciudad' },
  { key: 'FECHA_LETRAS', label: 'Fecha del auto (en letras)' },
];

const CAMPOS_APERTURA: CampoDoc[] = [
  { key: 'RADICADO', label: 'Radicado No.' },
  { key: 'INVESTIGADO', label: 'Investigado(a)' },
  { key: 'CARGO_ENTIDAD', label: 'Cargo y entidad' },
  { key: 'CIUDAD', label: 'Ciudad' },
  { key: 'FECHA_LETRAS', label: 'Fecha del auto (en letras)' },
];

export const DOC_INDAGACION_PREVIA: DocDecisionConfig = {
  plantillaId: 'INDAGACION_PREVIA',
  titulo: 'Auto de indagación previa (Art. 208)',
  campos: CAMPOS_INDAGACION,
  prefill: (exp) => base(exp),
};

export const DOC_AUTO_APERTURA: DocDecisionConfig = {
  plantillaId: 'AUTO_APERTURA',
  titulo: 'Auto de apertura de investigación (Art. 211-213)',
  campos: CAMPOS_APERTURA,
  prefill: (exp) => base(exp),
};

// Documentos de la decisión "Investigación disciplinaria": el auto que prorroga el
// término (Art. 213) y la diligencia de versión libre (num. 3, art. 112). Misma
// cabecera; FECHA_LETRAS = fecha del auto (desde la que corre la 2ª prórroga).
const CAMPOS_INVESTIGACION_DOC: CampoDoc[] = [
  { key: 'RADICADO', label: 'Radicado No.' },
  { key: 'INVESTIGADO', label: 'Investigado(a)' },
  { key: 'CARGO_ENTIDAD', label: 'Cargo y entidad' },
  { key: 'FECHA_QUEJA', label: 'Fecha de la queja / informe' },
  { key: 'CIUDAD', label: 'Ciudad' },
  { key: 'FECHA_LETRAS', label: 'Fecha del auto (en letras)' },
];

export const DOC_AUTO_PRORROGA: DocDecisionConfig = {
  plantillaId: 'AUTO_PRORROGA',
  titulo: 'Auto que prorroga la investigación (Art. 213)',
  campos: CAMPOS_INVESTIGACION_DOC,
  prefill: (exp) => base(exp),
};

export const DOC_VERSION_LIBRE: DocDecisionConfig = {
  plantillaId: 'VERSION_LIBRE',
  titulo: 'Versión libre (num. 3, art. 112)',
  campos: CAMPOS_INVESTIGACION_DOC,
  prefill: (exp) => base(exp),
};

/** Genera el .docx del documento como Blob (para previsualizar con docx-preview). */
export function blobDoc(
  cfg: DocDecisionConfig,
  datos: DatosDoc,
  opciones?: { marcaBorrador?: boolean },
): Promise<Blob> {
  return renderDocxBlob(PLANTILLAS[cfg.plantillaId].ruta, datos, opciones);
}

/** Descarga el documento en .docx con los datos indicados. */
export function descargarDoc(
  cfg: DocDecisionConfig,
  radicado: string,
  datos: DatosDoc,
  opciones?: { marcaBorrador?: boolean },
): Promise<void> {
  const slug = cfg.plantillaId.toLowerCase().replace(/_/g, '-');
  return generarDocx(PLANTILLAS[cfg.plantillaId].ruta, datos, `${slug}-${radicado || 'documento'}.docx`, opciones);
}
